import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Divider, Form, Header } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfoPopup from '~/components/InfoPopup'
import { ClimaRegDataType } from '~/api/services/climaProgram'
import { formatNumber } from '~/utils/helpers'
import ErrorIcon from '~/components/ErrorIcon'

const ACTIVE_YEAR = new Date().getFullYear()

interface Props {
  concentratedFeedsAmounts: ClimaRegDataType['concentratedFeedsAmounts']
  milkAmounts: ClimaRegDataType['milkAmounts']
}

const ChangesInLastThreeYears: React.FC<Props> = ({ concentratedFeedsAmounts, milkAmounts }) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()

  const watchChangesInLastThreeYears = useWatch({ control, name: 'changesInLastThreeYears' })

  const yearsForChanges = useMemo(() => {
    return [ACTIVE_YEAR - 3, ACTIVE_YEAR - 2, ACTIVE_YEAR - 1]
  }, [])

  useEffect(() => {
    if (!watchChangesInLastThreeYears) {
      setValue('changes', [])
    }
  }, [watchChangesInLastThreeYears])

  return (
    <>
      <Header as='h4'>{t('Changes in the last three years')}</Header>
      <Divider />
      <Controller
        control={control}
        name='changesInLastThreeYears'
        rules={{ required: true }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{t('Have there been changes in the last three years?')}</div>
              <InfoPopup
                content={t('Provide the milk yield for the last 3 years before the start of the project and the use of feed concentrate (per cow and year).')}
                styleIcon={{ fontSize: '1.2em' }}
              />
              <ErrorIcon control={control} name='changesInLastThreeYears' />
            </label>
            <div style={{ display: 'flex', gap: 20 }}>
              <Checkbox
                radio
                label={t('Yes')}
                checked={watchChangesInLastThreeYears === 1}
                onClick={() => setValue('changesInLastThreeYears', 1)}
              />
              <Checkbox
                radio
                label={t('No')}
                checked={watchChangesInLastThreeYears === 0}
                onClick={() => setValue('changesInLastThreeYears', 0)}
              />
            </div>
          </Form.Field>
        )}
      />

      {watchChangesInLastThreeYears === 1 && (
        <>
          {yearsForChanges.map((year, index) => (
            <div key={year} style={{ marginTop: '1em' }}>
              <Header as='h5'>{t('Year')} {year}</Header>
              <Form.Group widths='equal'>
                <FormFields.Select
                  name={`changes[${index}].avgMilkAmount`}
                  placeholder={t('Select')}
                  control={control}
                  options={milkAmounts?.map(option => ({ text: `${formatNumber(option.key, 0)} kg`, value: option.value }))}
                  withoutAsterisk
                  label={t('Average milk amount (kg/year) in year {{year}}', { year })}
                  typeInput='controled'
                  noErrorMessage
                  rules={{
                    required: true
                  }}
                />
                <FormFields.Select
                  name={`changes[${index}].yearlyConcentratedFeedAmountPerCow`}
                  placeholder={t('Select')}
                  withoutAsterisk
                  options={concentratedFeedsAmounts?.map(option => ({ text: `${formatNumber(option.key, 0)} kg`, value: option.value }))}
                  control={control}
                  label={t('Yearly concentrated feed amount per cow (kg) in year {{year}}', { year })}
                  noErrorMessage
                  rules={{
                    required: true
                  }}
                />
              </Form.Group>
              <Controller
                control={control}
                name={`changes[${index}].year`}
                defaultValue={year}
                render={() => <input type='hidden' />}
              />
            </div>
          ))}
        </>
      )}
    </>
  )
}

export default ChangesInLastThreeYears
